import type {Component, Editor} from 'grapesjs';
import type {AutoUpdateBrandingParams, AutoUpdateTextContentParams, EmailMakerBannerOptions} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import type {ConcurrentEditPayload, EmailMakerSaveErrorCode} from '@EmailMakerReact/lib/email-maker-storage-library';
import type {ColorControlDescriptor} from '@EmailMakerReact/layout/email-maker-right-panel/controls/color-control/color-control.types';
import type {EmbeddedEditorElementsToHide} from '@Libraries/embedded-editor';

/** Wire shape must stay in sync with EmailMakerLoadPayload::jsonSerialize on the PHP side. */
export interface EmailMakerImmutableStore {
  mode: EmailMakerMode;
  idEmailTemplate: string | null;
  templateName: string;
  idEmailMarketingCampaign: string | null;
  isApproved: boolean;
  isSharedInTeam: boolean;
  doesTemplateBelongToUser: boolean;
  doSetAutosaveActive: boolean;
  isEmailEditorAiTextGenerationEnabled: boolean;
  isEmailEditorAiImageGenerationViaPromptBoxEnabled: boolean;
  isEmailEditorAiDesignUpdatesViaPromptBoxEnabled: boolean;
  autoUpdateEmailContentParams: AutoUpdateEmailContentParams;
  puppeteerConfig: EmailMakerPuppeteerConfig | null;
  isSuperUser: boolean;
  isEmbedded: boolean;
  nextBtnCaption: string;
  elementsToHide: EmbeddedEditorElementsToHide[];
}

/** Wire shape must stay in sync with AutoUpdateEmailContentParams::toArray on the PHP side. */
export interface AutoUpdateEmailContentParams {
  autoUpdateEmailTemplateOnLoad: boolean;
  autoUpdateTextContentParams: AutoUpdateTextContentParams | null;
  autoUpdateBrandingParams: AutoUpdateBrandingParams | null;
}

/** Wire shape must stay in sync with EmailMakerPuppeteerConfig::jsonSerialize on the PHP side. */
export interface EmailMakerPuppeteerConfig {
  flow: 'TEMPLATE_AUTO_BRANDING';
}

/** Values must match Emailmaker.php MODE_START / MODE_LOAD / MODE_COPY. */
export enum EmailMakerMode {
  START = 'start',
  LOAD = 'load',
  COPY = 'copy',
}

export enum EmailMakerSaveState {
  SAVED = 'saved',
  SAVING = 'saving',
  NOT_SAVED = 'not-saved',
}

/** Ids double as GrapesJS device ids and are serialized into saved media queries. */
export enum EmailMakerCanvasDevice {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
}

export enum EditorViewport {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

/** Values are serialized into saved templates via the pmw-media-type prop. */
export enum EmailMakerMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
  ANIMATED_STICKER = 'animated_sticker',
  STILL_STICKER = 'still_sticker',
  ICON = 'icon',
}

export enum EmailMakerMediaImageModal {
  MY_UPLOADS = 'my-uploads-images',
  EXPLORE_MEDIA = 'explore-media-images',
  AI_IMAGES = 'ai-images',
}

export enum EmailMakerExportOption {
  COPY_EMAIL = 1,
  COPY_HTML = 2,
  DOWNLOAD_HTML = 3,
}

/** Recorded for any save failure carrying renderable detail (invalid links or a backend message); code is null when the backend code is unrecognized. Message-less failures surface through saveState alone. */
export interface EmailMakerSaveErrorState {
  code: EmailMakerSaveErrorCode | null;
  conflictPayload?: ConcurrentEditPayload;
  invalidUrls?: string[];
  /** Plain-text backend message (HTML stripped), safe to render as text. */
  message?: string;
}

/** DEFAULT shows the sector list; every other value replaces it with the named full-panel view. */
export enum RightPanelView {
  DEFAULT = 'default',
  KEYBOARD_SHORTCUTS = 'keyboard-shortcuts',
  COLOR_PALETTE = 'color-palette',
  COLUMNS_ALL = 'columns-all',
  COLOR_PICKER = 'color-picker',
}

/** The component + color descriptor the COLOR_PICKER overlay reads from and writes to; null when no color is being edited. */
export interface EmailMakerColorPickerTarget {
  component: Component;
  descriptor: ColorControlDescriptor;
}

export interface EmailMakerState {
  saveState: EmailMakerSaveState;
  saveError: EmailMakerSaveErrorState | null;
  banner: EmailMakerBannerOptions | null;
  loadFailed: boolean;
  isEditorLoading: boolean;
  selectedComponent: Component | null;
  editorViewport: EditorViewport;
  canvasDevice: EmailMakerCanvasDevice;
  autosaveEnabled: boolean;
  rightPanelView: RightPanelView;
  colorPickerTarget: EmailMakerColorPickerTarget | null;
}

export const EMAIL_MAKER_INITIAL_STATE: EmailMakerState = {
  saveState: EmailMakerSaveState.SAVED,
  saveError: null,
  banner: null,
  loadFailed: false,
  isEditorLoading: true,
  selectedComponent: null,
  editorViewport: EditorViewport.DESKTOP,
  canvasDevice: EmailMakerCanvasDevice.DESKTOP,
  autosaveEnabled: false,
  rightPanelView: RightPanelView.DEFAULT,
  colorPickerTarget: null,
};

export interface EmailMakerActions {
  save: () => Promise<boolean>;
  undo: () => void;
  redo: () => void;
  setCanvasDevice: (device: EmailMakerCanvasDevice) => void;
  selectComponent: (component: Component | null) => void;
  openRightPanelKeyboardShortcuts: () => void;
  openRightPanelColorPalette: () => void;
  openRightPanelColumnsAll: () => void;
  openRightPanelColorPicker: (target: EmailMakerColorPickerTarget) => void;
  closeRightPanelView: () => void;
}

export interface EmailMakerContextProps {
  editor: Editor | null;
  store: EmailMakerImmutableStore;
  state: EmailMakerState;
  actions: EmailMakerActions;
}

export interface EmailMakerReactProps {
  store: EmailMakerImmutableStore;
}
